// ============================================================
// SITE CONFIGURATION — change the business name, contact email
// and imagery here and the whole website updates automatically.
// ============================================================

export const SITE = {
  name: "Essex Black Cab",
  domain: "essexblackcab.co.uk",
  tagline: "London Black Cab drivers already travelling your way.",
  // Business email — configured with Apple iCloud+ on essexblackcab.co.uk.
  email: "bookings@essexblackcab.co.uk",
  // Legal trading entity — PLACEHOLDER, confirmed details required before launch.
  tradingEntity: "[Legal trading entity to be confirmed before launch]",
  fare: "£100",
  paymentMessage:
    "£100 fixed fare. Pay your driver directly inside the Black Cab at the end of your journey. Cash or card accepted. No advance payment required.",
  availabilityMessage:
    "All journeys are subject to driver availability and confirmation. Submitting a request does not guarantee a booking.",
};

export const WEB3FORMS = {
  endpoint: "https://api.web3forms.com/submit",
  // Public Web3Forms access key — safe to expose in frontend code.
  // Set REACT_APP_WEB3FORMS_ACCESS_KEY in your environment (see docs/SETUP-AND-COSTS.md).
  accessKey: process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || "",
};

export const IMAGES = {
  hero: {
    url: "https://images.unsplash.com/photo-1519587107946-3e3ae929150e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxsb25kb24lMjBibGFjayUyMGNhYnxlbnwwfHx8fDE3OTE5ODUyODB8MA&ixlib=rb-4.1.0&q=85",
    alt: "A traditional London black cab beside a red telephone box",
  },
  driver: {
    url: "https://images.unsplash.com/photo-1679416397704-6d16887cb40b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxsb25kb24lMjBibGFjayUyMGNhYnxlbnwwfHx8fDE3OTE5ODUyODB8MA&ixlib=rb-4.1.0&q=85",
    alt: "A London black cab driving through the City of London",
  },
  london: {
    url: "https://images.unsplash.com/photo-1549483249-f0b359d1e289?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjA1MTN8MHwxfHNlYXJjaHwxfHxsb25kb24lMjBza3lsaW5lJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MTk4NTI4MHww&ixlib=rb-4.1.0&q=85",
    alt: "The City of London skyline including the Gherkin",
  },
  essex: {
    url: "https://images.unsplash.com/photo-1661258825056-159d1fdfc886?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxjb3VudHJ5c2lkZSUyMHJvYWQlMjBldmVuaW5nfGVufDB8fHx8MTc5MTk4NTI4MHww&ixlib=rb-4.1.0&q=85",
    alt: "An open country road through the Essex countryside at dusk",
  },
};

export const ESSEX_AREAS = [
  "Great Dunmow",
  "Felsted",
  "Bishop's Stortford",
  "Stansted area",
  "Sawbridgeworth",
  "Harlow",
  "Nearby villages along the M11 corridor",
];

export const LONDON_AREAS = [
  "City of London",
  "Liverpool Street",
  "Bank",
  "Canary Wharf",
  "Central London",
  "Other London locations that suit an available driver",
];

export const NAV_LINKS = [
  { to: "/how-it-works", label: "How It Works" },
  { to: "/areas-covered", label: "Areas Covered" },
  { to: "/faqs", label: "FAQs" },
  { to: "/drivers", label: "Join as a Driver" },
  { to: "/contact", label: "Contact" },
];

export function generateBookingRef() {
  const rand = new Uint8Array(3);
  window.crypto.getRandomValues(rand);
  const code = Array.from(rand)
    .map((b) => "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"[b % 32])
    .join("")
    .slice(0, 5);
  return `M11-${code}`;
}

export function generateBookingKey() {
  const rand = new Uint8Array(16);
  window.crypto.getRandomValues(rand);
  return Array.from(rand)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}


// ---------------------------------------------------------------
// Privacy-safe area detection for driver job summaries.
// Order: (1) curated landmark/district/station names, (2) UK
// postcode outward-district mapping, (3) generic county/city words,
// (4) broad side fallback. Only ever outputs an area label — never
// a postcode, house number or street — and never guesses a specific
// place when nothing matches reliably.
// ---------------------------------------------------------------

const AREA_MAP = [
  // ---- Essex: M11 corridor towns & villages (specific first) ----
  { side: "essex", match: ["great dunmow", "dunmow"], label: "Great Dunmow area" },
  { side: "essex", match: ["felsted"], label: "Felsted area" },
  { side: "essex", match: ["bishop's stortford", "bishops stortford", "stortford"], label: "Bishop's Stortford area" },
  { side: "essex", match: ["stansted airport", "stansted"], label: "Stansted area" },
  { side: "essex", match: ["sawbridgeworth"], label: "Sawbridgeworth area" },
  { side: "essex", match: ["harlow"], label: "Harlow area" },
  { side: "essex", match: ["epping"], label: "Epping area" },
  { side: "essex", match: ["chipping ongar", "ongar"], label: "Ongar area" },
  { side: "essex", match: ["takeley"], label: "Takeley area" },
  { side: "essex", match: ["thaxted"], label: "Thaxted area" },
  { side: "essex", match: ["hatfield heath"], label: "Hatfield Heath area" },
  { side: "essex", match: ["sheering"], label: "Sheering area" },
  { side: "essex", match: ["matching"], label: "Matching area" },
  { side: "essex", match: ["chelmsford"], label: "Chelmsford area" },
  { side: "essex", match: ["braintree"], label: "Braintree area" },
  { side: "essex", match: ["saffron walden"], label: "Saffron Walden area" },
  { side: "essex", match: ["essex"], label: "Essex area" },
  // ---- London: landmarks, districts & stations (specific first) ----
  { side: "london", match: ["the shard", "shard", "london bridge", "borough"], label: "London Bridge / Shard area" },
  { side: "london", match: ["liverpool street"], label: "Liverpool Street area" },
  { side: "london", match: ["canary wharf", "isle of dogs"], label: "Canary Wharf area" },
  { side: "london", match: ["stratford"], label: "Stratford area" },
  { side: "london", match: ["heathrow"], label: "Heathrow area" },
  { side: "london", match: ["city airport"], label: "London City Airport area" },
  { side: "london", match: ["king's cross", "kings cross", "st pancras"], label: "King's Cross area" },
  { side: "london", match: ["euston"], label: "Euston area" },
  { side: "london", match: ["paddington"], label: "Paddington area" },
  { side: "london", match: ["victoria"], label: "Victoria area" },
  { side: "london", match: ["waterloo"], label: "Waterloo area" },
  { side: "london", match: ["tower bridge", "tower hill"], label: "Tower Bridge area" },
  { side: "london", match: ["moorgate"], label: "Moorgate area" },
  { side: "london", match: ["fenchurch"], label: "Fenchurch Street area" },
  { side: "london", match: ["blackfriars"], label: "Blackfriars area" },
  { side: "london", match: ["shoreditch"], label: "Shoreditch area" },
  { side: "london", match: ["islington"], label: "Islington area" },
  { side: "london", match: ["whitechapel", "aldgate"], label: "Whitechapel / Aldgate area" },
  { side: "london", match: ["westminster"], label: "Westminster area" },
  { side: "london", match: ["city of london", "the city"], label: "City of London area" },
  { side: "london", match: ["bank"], label: "Bank area" },
  { side: "london", match: ["central london", "west end", "soho", "covent garden", "oxford street", "oxford circus", "holborn", "marylebone", "fitzrovia"], label: "Central London area" },
  { side: "london", match: ["greenwich"], label: "Greenwich area" },
  { side: "london", match: ["london"], label: "London area" },
];

// UK postcode outward district → area (applied only when the postcode's
// side matches the journey side, so a CM code never labels a London leg).
const POSTCODE_MAP = [
  // Essex
  { side: "essex", codes: ["CM6"], label: "Great Dunmow area" },
  { side: "essex", codes: ["CM22", "CM23"], label: "Bishop's Stortford area" },
  { side: "essex", codes: ["CM24"], label: "Stansted area" },
  { side: "essex", codes: ["CM21"], label: "Sawbridgeworth area" },
  { side: "essex", codes: ["CM17", "CM18", "CM19", "CM20"], label: "Harlow area" },
  { side: "essex", codes: ["CM16"], label: "Epping area" },
  { side: "essex", codes: ["CM4", "CM5"], label: "Ongar area" },
  { side: "essex", codes: ["CM1", "CM2", "CM3"], label: "Chelmsford area" },
  { side: "essex", codes: ["CM7"], label: "Braintree area" },
  { side: "essex", codes: ["CB10", "CB11"], label: "Saffron Walden area" },
  // London
  { side: "london", codes: ["SE1"], label: "London Bridge / Shard area" },
  { side: "london", codes: ["EC2"], label: "Liverpool Street / Bank area" },
  { side: "london", codes: ["EC3", "EC4"], label: "City of London area" },
  { side: "london", codes: ["E14"], label: "Canary Wharf area" },
  { side: "london", codes: ["E15", "E20"], label: "Stratford area" },
  { side: "london", codes: ["E1"], label: "Whitechapel / Aldgate area" },
  { side: "london", codes: ["TW6"], label: "Heathrow area" },
  { side: "london", codes: ["SW1"], label: "Westminster area" },
  { side: "london", codes: ["N1"], label: "Islington area" },
  { side: "london", codes: ["E16"], label: "London City Airport area" },
  { side: "london", codes: ["N1C", "NW1"], label: "King's Cross / Euston area" },
  { side: "london", codes: ["W1", "WC1", "WC2", "EC1"], label: "Central London area" },
];

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function extractOutwardCode(address) {
  const m = (address || "").toUpperCase().match(/\b([A-Z]{1,2}\d{1,2}[A-Z]?)\s*(\d[A-Z]{2})?\b/);
  return m ? m[1] : null;
}

export function detectArea(address, side) {
  const a = ` ${(address || "").toLowerCase()} `;
  // 1. Curated landmark / district / station names
  for (const entry of AREA_MAP) {
    if (entry.side !== side) continue;
    if (entry.match.some((m) => new RegExp(`\\b${escapeRegExp(m)}\\b`).test(a))) {
      return entry.label;
    }
  }
  // 2. Postcode outward district. Exact outward match first (e.g. N1C),
  // then the base district for letter-suffixed codes (e.g. SW1A → SW1).
  const outward = extractOutwardCode(address);
  if (outward) {
    const base = outward.replace(/[A-Z]$/, "");
    const exact = POSTCODE_MAP.find((p) => p.side === side && p.codes.includes(outward));
    if (exact) return exact.label;
    const baseHit = POSTCODE_MAP.find((p) => p.side === side && p.codes.includes(base));
    if (baseHit) return baseHit.label;
  }
  // 3. Broad side fallback — never guesses a specific place
  return side === "essex" ? "Essex area" : "London area";
}